import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import * as _ from 'lodash';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { ConfigService } from 'src/app/modules/core/services/config.service';
import * as stringSimilarity from '@dentsply-sirona/string-similarity';
import { Options } from '../enums/options';
import { Option } from '../models/option.model';
import { OptionsService } from './options.service';
import { SessionService } from './session.service';
import { DateFormatService } from './dateFormat.service';
import { LocaleGraphqlService } from './locale-graphql.service';
import { distinctUntilChanged, filter, mergeMap, tap } from 'rxjs/operators';
import { User } from '../models/user.model';

/**
 * Locale service
 *
 * For current language and timezone Observable see SessionService
 */
@Injectable({
	providedIn: 'root'
})
export class LocaleService {
	// Default timezone
	protected _defaultTimezone: string;

	timezone$ = this.createTimezoneObservable();

	protected countryCodeSubject$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
	countryCode$ = this.creatCountryCodeObservable();

	/**
	 * Init services and languages
	 */
	constructor(
		// Additional services
		protected sessionService: SessionService,
		protected configService: ConfigService,
		protected optionsService: OptionsService,
		protected activatedRoute: ActivatedRoute,
		protected dateFormatService: DateFormatService,
		protected languageGraphqlService: LocaleGraphqlService
	) {
		this._defaultTimezone = this.configService.config.defaultTimezone;
		this.timezone$ = this.createTimezoneObservable();
		this.countryCode$ = this.creatCountryCodeObservable();
	}

	createTimezoneObservable(): Observable<string> {
		return this.sessionService.currentUserObservable
			.pipe(
				mergeMap((user) => {
					const timezone = this.getTimezone(user);
					if (user) {
						return this.getSuitableTimezone(timezone);
					}
					return of(timezone);
				})
			)
			.pipe(distinctUntilChanged());
	}

	creatCountryCodeObservable(): Observable<string> {
		return this.countryCodeSubject$.pipe(filter((code) => !!code)).pipe(distinctUntilChanged());
	}

	/**
	 * Init ConnectionDetails for register
	 * in some countries the captcha from Google is not allowed, so we have to check for country and captcha
	 */
	public getConnectionDetails(): Observable<{ countryCode: string; enableCaptcha: boolean }> {
		return this.languageGraphqlService.getConnectionDetails().pipe(
			tap((connectionDetails) => {
				this.countryCodeSubject$.next(connectionDetails.countryCode);
			})
		);
	}

	public getTimezone(user: User): string {
		let initialTimezone = this._defaultTimezone;

		// 1. Timezone of the user
		if (user?.timezone) {
			initialTimezone = user.timezone;
			// 2. Timezone of the company
		} else if (user?.company?.timezone?.id) {
			initialTimezone = user.company.timezone.id;
			// 4. Timezone from browser
		} else if (this.getTimezoneFromBrowser()) {
			initialTimezone = this.getTimezoneFromBrowser();
		}

		return initialTimezone;
	}

	/**
	 * Get most suitable timezone
	 */
	public getSuitableTimezone(desiredTimezone: string): Observable<string> {
		return new Observable<string>((subscriber) => {
			this.optionsService.getOptions([Options.TIMEZONE]).subscribe(
				(options) => {
					// Init
					const timezoneOptions: Option[] = options ? options[0] : [];

					// Find option in timezone options from server
					const clearedOptions = Array.isArray(timezoneOptions) ? timezoneOptions.filter((item) => item && item.id) : [];
					let option: { id: string } = _.find(clearedOptions, { id: desiredTimezone });

					// If no direct option can be found take the most similar option
					if (!option) {
						const optionStringArray: string[] = clearedOptions.map((obj) => obj.id);
						if (optionStringArray && optionStringArray.length) {
							option = { id: stringSimilarity.findBestMatch(desiredTimezone, optionStringArray).bestMatch.target };
						}
					}

					// Set suitableTimezone
					const suitableTimezone = option ? option.id : this._defaultTimezone;

					// Return suitableTimezone
					subscriber.next(suitableTimezone);
					subscriber.complete();
				},
				(error) => {
					subscriber.error(error);
				}
			);
		});
	}

	private getTimezoneFromBrowser() {
		const intl = this.getInternationalizationApi();

		if (
			intl &&
			intl.DateTimeFormat &&
			intl.DateTimeFormat() &&
			intl.DateTimeFormat().resolvedOptions &&
			intl.DateTimeFormat().resolvedOptions() &&
			intl.DateTimeFormat().resolvedOptions().timeZone
		) {
			return intl.DateTimeFormat().resolvedOptions().timeZone;
		}
	}

	private getInternationalizationApi() {
		return Intl;
	}
}
